import { Box, Button as ChakraButton, Icon } from "@chakra-ui/core";
import { transformAliasProps as tx } from "@chakra-ui/core/dist/Box/config";
import styled from "@emotion/styled";
import css from "@styled-system/css";
import { UnstyledCheck } from "atoms/Checkbox";
import { PSmall } from "atoms/typography";
import Link from "next/link";
import React, { FunctionComponent } from "react";
import buttonStyle from "./styles";

// Adding additional pseudo selector aliases.
// https://github.com/chakra-ui/chakra-ui/blob/%40chakra-ui/core%400.8.0/packages/chakra-ui/src/PseudoBox/index.js
const hoverAndBefore = "&:hover:before";
const activeAndBefore = "&:active:before, &[data-active=true]:before";

const ChakraButtonSuperPseudo = styled(ChakraButton)(
  ({ _hoverBefore, _activeBefore }) => {
    return css({
      [hoverAndBefore]: tx(_hoverBefore),
      [activeAndBefore]: tx(_activeBefore),
    });
  }
);
type ButtonProps =
  | React.ComponentProps<typeof ChakraButton>
  | React.ComponentProps<"a">;

// Custom control of the ButtonIcon
// https://github.com/chakra-ui/chakra-ui/blob/%40chakra-ui/core%400.8.0/packages/chakra-ui/src/Button/index.js#L11
const ButtonIcon = ({ icon, ...props }) => {
  if (typeof icon === "string") {
    return (
      <Icon aria-hidden="true" name={icon} color="currentColor" {...props} />
    );
  }
  return (
    <Box
      as={icon}
      data-custom-icon
      aria-hidden="true"
      color="currentColor"
      {...props}
    />
  );
};

const ButtonComponent = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      variant = "solid",
      variantColor = "pink",
      size = "lg",
      isDisabled,
      isLoading,
      leftIcon,
      children,
      dataCy,
      ...props
    },
    ref
  ) => {
    const buttonStyleProps = buttonStyle({
      variant,
      variantColor,
      size,
    });

    return (
      <ChakraButtonSuperPseudo
        variant={null}
        size={null}
        isDisabled={isDisabled}
        isLoading={isLoading}
        data-cy={dataCy}
        {...buttonStyleProps}
        {...props}
        ref={ref}
      >
        {leftIcon && !isLoading && (
          <ButtonIcon size={4} mr={2} icon={leftIcon} />
        )}
        {children}
      </ChakraButtonSuperPseudo>
    );
  }
);

const Button = ({
  isLink = false,
  isExternal = false,
  href = undefined,
  ...props
}: ButtonProps & {
  isLink?: boolean;
  isExternal?: boolean;
  href?: string;
  dataCy?: string;
}) => {
  if (isLink && !isExternal) {
    return (
      <Link href={href} passHref>
        <ButtonComponent as="a" {...props} />
      </Link>
    );
  }

  if (isLink && isExternal) {
    return (
      <ButtonComponent
        as="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      />
    );
  }

  return <ButtonComponent {...props} />;
};
Button.defaultProps = {
  size: "md",
  isLink: false,
  isExternal: false,
};

interface ButtonTooltipProps {
  isVisible: boolean;
  label: string;
}
export const ButtonTooltip: FunctionComponent<ButtonTooltipProps> = ({
  children,
  isVisible,
  label,
}) => {
  return (
    <Box position="relative" textAlign="center">
      {children}
      {isVisible && (
        <PSmall
          style={{
            position: "absolute",
            bottom: "0",
            width: "100%",
          }}
        >
          <Box as={UnstyledCheck} mr="4px" mb="4px" />
          {label}
        </PSmall>
      )}
    </Box>
  );
};

export default Button;
